/*
 * newsletter.js — submits the footer newsletter signup without a page reload.
 *
 * PURE ENHANCEMENT. The form is Shopify's native `{% form 'customer' %}`: with
 * no script it posts, the page reloads, and footer.liquid renders the outcome
 * (`form.posted_successfully?` / `form.errors`) into `[data-newsletter-status]`.
 *
 * This file makes the same POST with fetch, follows Shopify's redirect, and
 * lifts that same server-rendered status out of the response page. The message
 * is therefore always Shopify's own verdict, in whatever wording footer.liquid
 * gives it; nothing here decides success or invents text, and no Tailwind class
 * names appear here (`source(none)` does not scan .js).
 *
 * ONE SINK. A signup is idempotent: subscribing the same address twice leaves
 * one subscriber. So anything this script cannot read (a network failure, a bot
 * challenge page, a response with no status) hands the submit back to the
 * browser, which does exactly what it would have done without this file. The
 * challenge case matters: Shopify can answer a customer form with an hCaptcha
 * page the visitor must complete, and only a real navigation can show it.
 */

const FORM_SELECTOR = 'form[data-newsletter-form]'
const STATUS_SELECTOR = '[data-newsletter-status]'

const bound = new WeakSet()
/* Forms handed back to the browser, so the fallback is not intercepted again. */
const native = new WeakSet()

function showStatus(form, fresh) {
  const status = form.querySelector(STATUS_SELECTOR)
  if (!status) return
  status.textContent = fresh.textContent.trim()
  status.setAttribute('data-state', fresh.getAttribute('data-state'))
  status.hidden = false
}

function bindForm(form) {
  if (bound.has(form)) return
  bound.add(form)

  form.addEventListener('submit', (event) => {
    if (native.has(form)) return
    const action = form.getAttribute('action')
    if (!action) return

    let body
    try {
      body = new FormData(form)
    } catch {
      return
    }
    event.preventDefault()

    const submitter = event.submitter
    if (submitter) submitter.setAttribute('aria-busy', 'true')

    function fallback() {
      native.add(form)
      if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined)
      else form.submit()
    }

    /* The fragment (`#contact_form`) is for the no-script reload only. */
    fetch(action.split('#')[0], { method: 'POST', headers: { Accept: 'text/html' }, body: body })
      .then((response) => {
        if (!response.ok) throw new Error('contact answered ' + response.status)
        if (/\/challenge(\b|$|\?)/.test(response.url || '')) throw new Error('bot challenge')
        return response.text()
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html')
        const fresh = doc.querySelector(FORM_SELECTOR + ' ' + STATUS_SELECTOR + '[data-state]')
        if (!fresh) throw new Error('no newsletter status in the response')
        showStatus(form, fresh)
        if (fresh.getAttribute('data-state') === 'success') {
          const email = form.querySelector('input[type="email"]')
          if (email) email.value = ''
        }
        if (submitter) submitter.removeAttribute('aria-busy')
      })
      .catch(() => {
        if (submitter) submitter.removeAttribute('aria-busy')
        fallback()
      })
  })
}

function initAll(scope) {
  for (const form of scope.querySelectorAll(FORM_SELECTOR)) bindForm(form)
}

initAll(document)

/* The theme editor swaps section markup in and out with no page load. */
document.addEventListener('shopify:section:load', (event) => initAll(event.target))
